import { audioAt } from './replayEngine.js';

// Audio is fetched with Bearer authorization and converted to a short-lived
// object URL. Never attach a raw private-storage URL directly to an <audio> tag.
export const createReplayAudioClock = ({
  timeline,
  loadBlob,
  AudioClass = typeof Audio !== 'undefined' ? Audio : null,
  createUrl = (blob) => URL.createObjectURL(blob),
  revokeUrl = (url) => URL.revokeObjectURL(url),
  onError = () => {},
}) => {
  if (!Array.isArray(timeline) || typeof loadBlob !== 'function' || !AudioClass) {
    throw new TypeError('Replay audio needs a timeline, authenticated loader and audio element');
  }
  const element = new AudioClass();
  element.preload = 'auto';
  let currentSegmentIndex = null;
  let currentUrl = null;
  let requestId = 0;
  let inFlight = null;
  let disposed = false;
  let playingRequested = false;
  let requestedTimeMs = 0;

  const release = () => {
    element.pause();
    if (currentUrl) {
      revokeUrl(currentUrl);
      currentUrl = null;
    }
    if (typeof element.removeAttribute === 'function') element.removeAttribute('src');
    else element.src = '';
    element.load?.();
    currentSegmentIndex = null;
  };

  const sync = async (timeMs, playing = false, playbackRate = 1) => {
    if (disposed) return;
    requestedTimeMs = Math.max(0, timeMs);
    playingRequested = Boolean(playing);
    element.playbackRate = playbackRate;
    const target = audioAt(timeline, requestedTimeMs);
    if (!target) {
      requestId++;
      inFlight?.abort();
      inFlight = null;
      if (currentSegmentIndex !== null) release();
      return;
    }
    if (currentSegmentIndex !== target.segmentIndex) {
      const myRequestId = ++requestId;
      inFlight?.abort();
      inFlight = new AbortController();
      release();
      // Reserve the index before awaiting the HTTP fetch. Otherwise a
      // 60fps replay clock would restart the same request every frame.
      currentSegmentIndex = target.segmentIndex;
      try {
        const blob = await loadBlob(target.segmentIndex, inFlight.signal);
        if (disposed || myRequestId !== requestId) return;
        currentUrl = createUrl(blob);
        currentSegmentIndex = target.segmentIndex;
        element.src = currentUrl;
        element.load?.();
      } catch (error) {
        if (myRequestId === requestId) {
          currentSegmentIndex = null;
          if (!disposed && error?.name !== 'AbortError') onError(error);
        }
        return;
      }
    }
    if (disposed || !currentUrl || currentSegmentIndex !== target.segmentIndex) return;
    const desired = (requestedTimeMs - target.startMs) / 1000;
    // The game timeline owns the playback clock. Correct drift to events
    // when encoded Opus packet timing differs from MediaRecorder wall-time.
    if (!Number.isFinite(element.currentTime)
      || Math.abs(element.currentTime - desired) > 0.35) {
      element.currentTime = Math.max(0, desired);
    }
    if (!playingRequested) {
      element.pause();
    } else if (element.paused) {
      try { await element.play(); }
      catch (error) { if (!disposed) onError(error); }
    }
  };

  const stop = () => {
    requestId++;
    inFlight?.abort();
    inFlight = null;
    playingRequested = false;
    release();
  };
  const dispose = () => {
    disposed = true;
    stop();
  };

  return {
    sync, stop, dispose,
    get element() { return element; },
    get segmentIndex() { return currentSegmentIndex; },
  };
};
